// Composite (bileşik) soru renderer'ı — M15 §4.1.
// Parçalar etiket + ince çizgiyle ayrılır (kutu DEĞİL — kartın kendi çerçevesi "tek
// yanıt" işini yapıyor; kutu repeater'ın "birbirinin muadili" sinyalini verirdi, U1).
// Her parça kendi tipinin mevcut renderer'ını QuestionInput üzerinden aynen kullanır.
//
// D6 — parça yazımları HEP fonksiyonel setValue ile: AttachmentUploader'ın 2.5sn
// poll'ünden gelen gecikmeli onChange bayat closure ile diğer parçaları silerdi;
// updater store-yazım anındaki güncel kaydı okur.

import { readCompositeParts, type QuestionSnapshot } from "@mahir/shared";
import { useFormFillStore } from "../../../stores/form-fill-store";
import { QuestionInput } from "../QuestionInput";

interface Props {
  question: QuestionSnapshot;
  value: unknown;
}

export function CompositeRenderer({ question, value }: Props): JSX.Element {
  const setValue = useFormFillStore((s) => s.setValue);
  const partErrors = useFormFillStore((s) => s.errorsByCode[question.code]);

  const parts = readCompositeParts(question.options);
  if (!parts) {
    return (
      <p style={{ color: "var(--color-text-2)", fontSize: "0.82rem" }}>
        Bu sorunun parça tanımı okunamadı.
      </p>
    );
  }

  // §7.2 şekil uyuşmazlığı savunması: tip değişimi sonrası eski (obje olmayan) değer —
  // parçalar boş render + tek satır uyarı, çökme yok.
  const isRecord = value !== null && typeof value === "object" && !Array.isArray(value);
  const record = isRecord ? (value as Record<string, unknown>) : {};
  const shapeMismatch = !isRecord && value !== null && value !== undefined;

  const writePart = (partKey: string, partValue: unknown): void => {
    setValue(question.code, (prev: unknown) => {
      const base =
        prev !== null && typeof prev === "object" && !Array.isArray(prev)
          ? (prev as Record<string, unknown>)
          : {};
      return { ...base, [partKey]: partValue };
    });
  };

  // B5 fix — hiçbir tanımlı parçaya eşleşmeyen partKey'li hatalar (ör. önceki yıldan
  // kopyalanan eski parça kodu → "Bilinmeyen parça kodu") kartın üstünde gösterilir;
  // aksi halde ne parça altına ne soru düzeyine düşüp görünmez kalıyorlardı.
  const knownKeys = new Set(parts.map((p) => p.key));
  const orphanErrors = (partErrors ?? []).filter(
    (e) => e.partKey !== undefined && !knownKeys.has(e.partKey),
  );

  return (
    <div>
      {shapeMismatch && (
        <p
          className="mb-2"
          style={{ color: "var(--color-warning)", fontSize: "0.78rem" }}
          role="status"
        >
          Bu sorunun tipi değişmiş; önceki yanıt gösterilemiyor.
        </p>
      )}
      {orphanErrors.map((e, i) => (
        <p
          key={`orphan-${i}`}
          className="mb-2"
          style={{ color: "var(--color-error)", fontSize: "0.78rem" }}
          role="alert"
        >
          {e.message}
        </p>
      ))}
      {parts.map((part, i) => {
        const labelId = `part-label-${question.code}-${part.key}`;
        const errors = (partErrors ?? []).filter((e) => e.partKey === part.key);
        // Parça için sentetik snapshot: tip + options + etiket parçadan, kimlik
        // (id/code/sectionId) sorudan — AttachmentUploader presign'ı soru koduyla atar.
        const partQuestion: QuestionSnapshot = {
          ...question,
          type: part.options.type,
          options: part.options,
          required: part.required,
          label: part.label,
          helperText: part.helperText ?? null,
        };
        return (
          <div
            key={part.key}
            data-part-key={part.key}
            role="group"
            aria-labelledby={labelId}
            style={
              i > 0
                ? { borderTop: "1px solid var(--color-border)", marginTop: 14, paddingTop: 14 }
                : undefined
            }
          >
            <span
              id={labelId}
              style={{
                display: "block",
                fontSize: "0.8rem",
                fontWeight: 600,
                // Ayırt edici font ailesi DEĞİL (DR4 — üç font değişkeni de aynı aile);
                // zorunlu/opsiyonel ayrımı renkle: text vs text-2.
                color: part.required ? "var(--color-text)" : "var(--color-text-2)",
                marginBottom: 6,
              }}
            >
              {part.label}
            </span>
            <QuestionInput
              question={partQuestion}
              value={record[part.key]}
              onChange={(v) => writePart(part.key, v)}
            />
            {part.helperText && (
              <p
                className="mt-1"
                style={{ color: "var(--color-text-2)", fontSize: "0.75rem", fontStyle: "italic" }}
              >
                {part.helperText}
              </p>
            )}
            {errors.map((e, j) => (
              <p
                key={j}
                className="mt-1"
                style={{ color: "var(--color-error)", fontSize: "0.78rem" }}
                role="alert"
              >
                {e.message}
              </p>
            ))}
          </div>
        );
      })}
    </div>
  );
}
